<div class="users-form-page page-container">
  <div class="form-container">
    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">{{ isEditMode ? 'Editar Usuario' : 'Registro' }}</h1>
      <p class="page-subtitle">
        {{
          isEditMode
            ? 'Actualiza la información del usuario'
            : 'Crear nuevo usuario como partner o como administrador.'
        }}
      </p>
    </div>

    @if (error) {
      <div class="error-message">
        {{ error }}
      </div>
    }

    @if (isFetching) {
      <div class="loading-state flex flex-col items-center justify-center py-24">
        <div class="loading-spinner"></div>
        <p class="mt-4 text-slate-500 dark:text-slate-400 font-medium">
          Cargando datos del usuario...
        </p>
      </div>
    } @else {
      <form [formGroup]="userForm" (ngSubmit)="onSubmit()" class="user-form">
        <!-- Welcome/Instruction Banner (Solo en modo creación) -->
        @if (!isEditMode) {
          <div class="mb-8">
            <div
              class="rounded-xl border border-gray-100 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900/50"
            >
              <h3 class="mb-3 text-lg font-semibold text-gray-800 dark:text-white">
                ¿Cómo crear un nuevo usuario?
              </h3>
              <div class="grid gap-4 text-sm text-gray-600 dark:text-gray-300 md:grid-cols-2">
                <div class="flex gap-3">
                  <div
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-500 dark:bg-red-500/20 dark:text-red-400"
                  >
                    1
                  </div>
                  <p>
                    Introduce los <strong>datos personales</strong> (nombre, email y teléfono) en la
                    primera sección.
                  </p>
                </div>
                <div class="flex gap-3">
                  <div
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-500 dark:bg-red-500/20 dark:text-red-400"
                  >
                    2
                  </div>
                  <p>
                    Elige el <strong>rol</strong> del usuario para definir sus permisos en la
                    plataforma.
                  </p>
                </div>
                <div class="flex gap-3">
                  <div
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-500 dark:bg-red-500/20 dark:text-red-400"
                  >
                    3
                  </div>
                  <p>
                    Si es un <strong>Partner</strong>, selecciona su contacto de SDi y la empresa
                    asociada de Hubspot.
                  </p>
                </div>
                <div class="flex gap-3">
                  <div
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-500 dark:bg-red-500/20 dark:text-red-400"
                  >
                    4
                  </div>
                  <p>
                    El sistema mostrará automáticamente los datos de la empresa almacenados en
                    <strong>Hubspot</strong>.
                  </p>
                </div>
                <div class="flex gap-3">
                  <div
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-500 dark:bg-red-500/20 dark:text-red-400"
                  >
                    5
                  </div>
                  <p>
                    Tras la creación, se podrá enviar un <strong>email de invitación</strong> al
                    usuario para verificar su cuenta.
                  </p>
                </div>
                <div class="flex gap-3">
                  <div
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-500 dark:bg-red-500/20 dark:text-red-400"
                  >
                    6
                  </div>
                  <p>
                    El usuario podrá establecer una <strong>nueva contraseña</strong> y acceder al
                    panel de partners desde el mismo enlace.
                  </p>
                </div>
              </div>
              <!-- Legend for locked fields -->
              <div
                class="mt-6 flex items-center gap-2 border-t border-gray-100 pt-5 text-[13px] text-gray-500 italic dark:border-gray-800 dark:text-gray-400"
              >
                <fa-icon [icon]="faLock" class="text-gray-400 dark:text-gray-500"></fa-icon>
                <p>
                  Los campos marcados con este icono son de
                  <strong>solo lectura</strong> (sincronizados automáticamente con el Partner en
                  Hubspot).
                </p>
              </div>
            </div>
          </div>
        }

        <!-- Contact Person Section -->
        <div class="form-section">
          <div class="section-header">
            <fa-icon [icon]="faUser" class="section-icon"></fa-icon>
            <h2 class="section-title">Datos Personales</h2>
          </div>

          <div class="form-group">
            <label for="firstName">Nombre completo</label>
            <input
              type="text"
              id="firstName"
              class="app-input"
              placeholder="Juan"
              formControlName="name"
              [class.invalid]="f['name'].invalid && f['name'].touched"
            />
            @if (f['name'].invalid && f['name'].touched) {
              <div class="error-text">
                @if (f['name'].errors?.['required']) {
                  <span>El nombre es obligatorio</span>
                }
                @if (f['name'].errors?.['minlength']) {
                  <span>El nombre debe tener al menos 2 caracteres</span>
                }
              </div>
            }
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">Email</label>
              <input
                type="email"
                id="email"
                class="app-input"
                placeholder="juan.perez@example.com"
                formControlName="email"
                [class.invalid]="f['email'].invalid && f['email'].touched"
              />
              @if (f['email'].invalid && f['email'].touched) {
                <div class="error-text">
                  @if (f['email'].errors?.['required']) {
                    <span>El email es obligatorio</span>
                  }
                  @if (f['email'].errors?.['email']) {
                    <span>Email inválido</span>
                  }
                </div>
              }
            </div>

            <div class="form-group">
              <label for="phone">Número de Teléfono</label>
              <input
                type="tel"
                id="phone"
                class="app-input"
                placeholder="+34 600 000 000"
                formControlName="phone"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="roles">Rol</label>
              <select
                id="roles"
                class="app-input"
                (change)="onRoleChange($event)"
                [value]="f['roles'].value[0]"
              >
                <option value="partner">Partner</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div class="form-group">
              <label for="type">
                Modelo de Relación
                <button
                  type="button"
                  class="info-icon-btn"
                  (click)="openRelationshipTypeInfoModal()"
                  aria-label="Información sobre los modelos de relación"
                >
                  <fa-icon [icon]="faCircleInfo"></fa-icon>
                </button>
              </label>
              <select id="type" class="app-input" formControlName="type">
                <option value="">-- Seleccionar tipo --</option>
                @for (option of typeOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (f['type'].invalid && f['type'].touched) {
                <div class="error-text">
                  <span>El tipo es obligatorio</span>
                </div>
              }
            </div>
          </div>

          <div class="form-row">
            @if (f['roles'].value.includes('partner')) {
              <div class="form-group">
                <label for="sdi_contact_id">Contacto SDi</label>
                <select
                  id="sdi_contact_id"
                  class="app-input"
                  formControlName="sdi_contact_id"
                  [disabled]="isSDiContactsLoading"
                >
                  <option value="">
                    {{
                      isSDiContactsLoading ? 'Cargando contactos...' : '-- Seleccionar contacto --'
                    }}
                  </option>
                  @for (contact of sdiContacts; track contact.id) {
                    <option [value]="contact.id">{{ contact.name }}</option>
                  }
                </select>
              </div>
            }
          </div>

          @if (f['roles'].value.includes('partner')) {
            <div class="form-group mb-4">
              <label for="partner_id">Partner Asociado</label>
              <app-custom-select
                id="partner_id"
                formControlName="partner_id"
                [options]="partnerOptions"
                [loading]="isPartnersLoading"
                [loadingMore]="loadingMorePartners"
                [hasMore]="partnersPage < partnersTotalPages"
                [showSearch]="true"
                [icon]="faBuilding"
                placeholder="-- Seleccionar Partner --"
                searchPlaceholder="Buscar por nombre..."
                (searchChange)="onPartnerSearch($event)"
                (loadMore)="onPartnerLoadMore()"
              ></app-custom-select>
            </div>
          }
        </div>

        @if (f['roles'].value.includes('partner')) {
          <!-- Company Information Section -->
          <div class="form-section">
            <div class="section-header">
              <fa-icon [icon]="faBuilding" class="section-icon"></fa-icon>
              <h2 class="section-title">Información de la Empresa</h2>
            </div>

            <div class="form-group">
              <label for="company">
                <fa-icon
                  [icon]="faLock"
                  class="ml-1 text-gray-400"
                  title="Campo bloqueado"
                ></fa-icon>
                Nombre de la Empresa
              </label>
              <input
                type="text"
                id="company"
                class="app-input"
                placeholder="Business Corporation"
                [value]="company_name"
                readonly
              />
            </div>

            <div class="form-group">
              <label for="fiscal_address">
                <fa-icon
                  [icon]="faLock"
                  class="ml-1 text-gray-400"
                  title="Campo bloqueado"
                ></fa-icon>
                Dirección Fiscal
              </label>
              <textarea
                id="fiscal_address"
                class="app-input"
                placeholder="Paseo de la paz, 3, Ciudad, País, 26007"
                [value]="fiscal_address"
                rows="3"
                readonly
              ></textarea>
            </div>

            <div class="form-group">
              <label for="createdate">
                <fa-icon
                  [icon]="faLock"
                  class="ml-1 text-gray-400"
                  title="Campo bloqueado"
                ></fa-icon>
                Fecha de Alta
              </label>
              <input
                type="text"
                id="createdate"
                class="app-input"
                placeholder="Fecha de Alta"
                [value]="createdate"
                readonly
              />
            </div>
          </div>

          <!-- Partner Section -->
          <div class="form-section">
            <div class="section-header">
              <fa-icon [icon]="faUniversity" class="section-icon"></fa-icon>
              <h2 class="section-title">Partner</h2>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group">
                <label for="fixed_commission">
                  <fa-icon
                    [icon]="faLock"
                    class="ml-1 text-gray-400"
                    title="Campo bloqueado"
                  ></fa-icon>
                  Comisiones fijas (%)
                </label>
                <input
                  type="text"
                  id="fixed_commission"
                  class="app-input"
                  readonly
                  [value]="fixed_commission"
                />
              </div>

              <div class="form-group">
                <label for="recurring_commission">
                  <fa-icon
                    [icon]="faLock"
                    class="ml-1 text-gray-400"
                    title="Campo bloqueado"
                  ></fa-icon>
                  Comisiones recurrentes (%)
                </label>
                <input
                  type="text"
                  id="recurring_commission"
                  class="app-input"
                  readonly
                  [value]="recurring_commission"
                />
              </div>

            </div>
          </div>
        }

        <div class="form-actions">
          <button
            type="button"
            (click)="onCancel()"
            class="app-btn app-btn--secondary"
            [disabled]="isLoading"
          >
            Cancelar
          </button>
          <button type="submit" class="app-btn app-btn--primary" [disabled]="isLoading">
            {{ isLoading ? 'Guardando...' : isEditMode ? 'Actualizar Usuario' : 'Crear Usuario' }}
          </button>
        </div>
      </form>
    }
  </div>
</div>

@if (showRelationshipTypeInfoModal) {
  <div class="modal-overlay" (click)="closeRelationshipTypeInfoModal()">
    <div class="modal-container" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <fa-icon [icon]="faCircleInfo"></fa-icon>
          </div>
          <h2 class="modal-title">Modelos de Relación</h2>
        </div>
        <button class="btn-close" (click)="closeRelationshipTypeInfoModal()">
          <fa-icon [icon]="faXmark"></fa-icon>
        </button>
      </div>

      <div class="modal-body">
        <dl class="relationship-type-list">
          <div class="relationship-type-item">
            <dt>Referenciador</dt>
            <dd>
              Únicamente identifica oportunidades y las deriva a SDi. Puede optar a una comisión
              del 10% del proyecto, pero nunca participa en el negocio recurrente.
            </dd>
          </div>
          <div class="relationship-type-item">
            <dt>Distribuidor</dt>
            <dd>
              Comercializa productos o servicios de SDi y puede participar o no en la implantación.
              Las condiciones económicas se regulan en su contrato de distribución y puede
              participar en el negocio recurrente.
            </dd>
          </div>
          <div class="relationship-type-item">
            <dt>Partner Estratégico</dt>
            <dd>
              Alianzas estratégicas que aportan valor al negocio, aunque no necesariamente exista
              una venta directa.
            </dd>
          </div>
          <div class="relationship-type-item">
            <dt>Administrador</dt>
            <dd>Usuario administrador de la plataforma, encargado de la gestión interna.</dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
}
